/**
 * @file FormLayout.tsx
 * @description Раскладка форм панелей вида сайта на широком экране
 * (phase19.16 Р1, §7.16 фазы: разделы конструктора — на всю ширину окна,
 * SC56 «две колонки» шире).
 *
 * Считается от ширины самой панели (container queries Tailwind 4), а не
 * окна: одна и та же панель стоит и на отдельной странице, и в узкой
 * колонке будущей оболочки «Сайт → Конструктор».
 *  - `FieldsWithPreview` — поля слева, образец («Так в поиске», «Так во
 *    вкладке») справа, когда панели хватает места (от 48rem); иначе образец
 *    под полями, как на телефоне;
 *  - `FieldRow` — короткие поля в ряд (от 36rem — по два, `cols={3}` — по
 *    три от 48rem); описание и длинные поля ставятся вне ряда.
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface FieldsWithPreviewProps {
  children: ReactNode;
  /** Образец результата; рисуется справа от полей на широкой панели. */
  preview: ReactNode;
  className?: string;
}

export function FieldsWithPreview({ children, preview, className }: FieldsWithPreviewProps) {
  return (
    <div className={cn("@container min-w-0", className)}>
      <div className="grid items-start gap-5 @3xl:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] @3xl:gap-8">
        <div className="@container flex min-w-0 flex-col gap-5">{children}</div>
        <div className="flex min-w-0 flex-col gap-3 @3xl:sticky @3xl:top-[calc(var(--shell-header-h,4rem)+1rem)]">
          {preview}
        </div>
      </div>
    </div>
  );
}

interface FieldRowProps {
  children: ReactNode;
  /** Сколько полей в ряд на широкой панели. */
  cols?: 2 | 3;
}

/** Ряд коротких полей; родитель должен быть `@container` (см. `FieldsWithPreview`). */
export function FieldRow({ children, cols = 2 }: FieldRowProps) {
  return (
    <div className={cn("grid items-start gap-5", cols === 2 ? "@xl:grid-cols-2" : "@xl:grid-cols-2 @3xl:grid-cols-3")}>
      {children}
    </div>
  );
}
